import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  forwardRef,
} from 'react';

import style from './CenteredAppNav.module.css';

import { useViewport } from '../../../hooks';
import { CloseAlt, MenuToggle } from '../../../icons';
import { Link } from '../../Link';
import { Logo } from '../../Logo';
import { NavItem } from '../../NavItem';
import { Text } from '../../Text';

export const LogoWithMenuControl = forwardRef<
  HTMLButtonElement,
  {
    isOpen: boolean;
    logo?: ReactNode;
    logoTo: string;
    menuId: string;
    setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  }
>(({ isOpen, logo, logoTo, menuId, setIsMenuOpen }, ref) => {
  const viewport = useViewport();

  const label = isOpen
    ? 'Close the Navigation Menu'
    : 'Open the Navigation Menu';

  const logoComponent = (
    <Link to={logoTo} onClick={() => setIsMenuOpen(false)}>
      {logo ?? <Logo variant="product" size={viewport.lg ? 50 : 48} />}
    </Link>
  );

  return (
    <div className={style.logoWithMenuButton}>
      <NavItem
        type="button"
        aria-controls={menuId}
        aria-label={label}
        buttonRef={ref}
        hidden={viewport.lg}
        label={isOpen ? 'Close' : 'Menu'}
        onClick={e => {
          e.preventDefault();
          setIsMenuOpen(currentValue => !currentValue);
        }}
        // We add a `tabIndex` to workaround an issue with the focus trap when menu is open.
        // See https://app.shortcut.com/farmersdog/story/123953
        tabIndex={0}
      >
        {isOpen ? <CloseAlt /> : <MenuToggle />}
        <Text variant="heading-16" bold>
          {isOpen ? 'Close' : 'Menu'}
        </Text>
      </NavItem>
      {logoComponent}
    </div>
  );
});
